import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { QRCode } from "antd";
import { useAuth } from "../context/AuthContext";
import apiClient from "../utils/config/apiClient";
import BtnSpinner from "../components/common/btnSpinners";
import dedalogo from "../assets/dedaImg/dedalogo.png";
import dedaIcon from "../assets/dedaImg/deda-icon.png";
import dedaBg from "../assets/dedaImg/s4 (1).jpg";
import useProfileStore from "../store/patient/profileStore";
import useManagePatient from "../hooks/patientDashboard/useManagePatient";
import { toast } from "sonner";
import { FaEye, FaEyeSlash, FaKey, FaQrcode, FaSyncAlt, FaMobileAlt } from "react-icons/fa";


export default function Signin2() {
  const { login } = useAuth();
  const { saveprofile } = useProfileStore();
  const { findPatientAlt } = useManagePatient();
  const navigate = useNavigate();

  // Store volatile callbacks in refs so effects do not re-run on every render
  const loginRef = useRef(login);
  const saveprofileRef = useRef(saveprofile);
  const findPatientAltRef = useRef(findPatientAlt);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    loginRef.current = login;
    saveprofileRef.current = saveprofile;
    findPatientAltRef.current = findPatientAlt;
    navigateRef.current = navigate;
  });

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // QR Code Web Login State
  const [loginMode, setLoginMode] = useState("password"); // "password" | "qr"
  const [qrData, setQrData] = useState("");
  const [qrSessionId, setQrSessionId] = useState("");
  const [qrStatus, setQrStatus] = useState("idle"); // "idle" | "loading" | "pending" | "scanned" | "authorized" | "expired"
  const [timeLeft, setTimeLeft] = useState(120);

  const fetchQrSession = useCallback(async () => {
    setQrStatus("loading");
    try {
      const res = await apiClient.post("/public/portal?url=auth/qr/init");
      if (res.data?.success && res.data?.sessionId) {
        setQrSessionId(res.data.sessionId);
        setQrData(res.data.qrData);
        setQrStatus("pending");
        setTimeLeft(res.data.expiresInSeconds || 120);
      } else {
        toast.error("Failed to generate QR code session.");
        setQrStatus("expired");
      }
    } catch (err) {
      console.error("QR init error:", err);
      toast.error("Could not initialize QR login.");
      setQrStatus("expired");
    }
  }, []);

  // Fetch QR session strictly once when user first opens QR mode and state is idle
  useEffect(() => {
    if (loginMode === "qr" && qrStatus === "idle") {
      fetchQrSession();
    }
  }, [loginMode, qrStatus, fetchQrSession]);

  // Countdown timer effect
  useEffect(() => {
    if (loginMode !== "qr" || (qrStatus !== "pending" && qrStatus !== "scanned")) return;
    if (timeLeft <= 0) {
      setQrStatus("expired");
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setQrStatus("expired");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [loginMode, qrStatus, timeLeft]);

  // Polling QR code session status
  useEffect(() => {
    if (loginMode !== "qr" || !qrSessionId || qrStatus === "expired" || qrStatus === "authorized") {
      return;
    }

    let isMounted = true;
    let isChecking = false;

    const checkStatus = async () => {
      // Pause polling if user switches tabs to save server resources
      if (document.hidden || isChecking || !isMounted) return;
      isChecking = true;

      try {
        const res = await apiClient.get(`/public/portal?url=auth/qr/status/${qrSessionId}`);
        if (!isMounted || !res.data) return;

        console.log("[QR_POLL] status response:", res.data);

        if (res.data.status === "scanned") {
          setQrStatus((prev) => (prev !== "scanned" ? "scanned" : prev));
        } else if (res.data.status === "expired") {
          setQrStatus("expired");
        } else if (res.data.status === "rejected") {
          toast.error("Login request was cancelled on mobile device.");
          setQrStatus("expired");
        } else if (res.data.status === "authorized" && res.data.token) {
          setQrStatus("authorized");
          toast.success(`Welcome ${res.data.user?.other_names || ""} ${res.data.user?.surname || ""}`);

          const fullUserData = {
            ...res.data.user,
            token: res.data.token,
            timestamp: new Date().toISOString(),
          };

          localStorage.setItem("user", JSON.stringify(fullUserData));
          localStorage.setItem("token", res.data.token);
          localStorage.setItem("tokenV2", res.data.token);
          localStorage.setItem("userId", res.data.user?.id);
          if (res.data.user?.email) {
            localStorage.setItem("localEmail", res.data.user.email);
          }

          if (saveprofileRef.current) {
            saveprofileRef.current(fullUserData);
          }
          if (loginRef.current) {
            await loginRef.current(fullUserData);
          }
          navigateRef.current?.("/dashboard/overview");
        }
      } catch (err) {
        console.warn("QR status check warning:", err);
      } finally {
        isChecking = false;
      }
    };

    // Immediate check
    checkStatus();
    const pollInterval = setInterval(checkStatus, 2000);

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
    };
  }, [loginMode, qrSessionId, qrStatus]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    localStorage.setItem("localEmail", userName);

    try {
      const response = await apiClient.post("/public/portal?url=auth/login/patient", {
        email: userName,
        password,
      });

      if (!response.data) {
        toast.error("Unable to sign in right now.");
        setLoading(false);
        return;
      }

      if (response.data.otp === true) {
        navigate("/pages/verify");
        setLoading(false);
        return;
      }

      if (response.data.token) {
        toast.success(`Welcome ${response.data.other_names} ${response.data.surname}`);

        const fullUserData = {
          ...response.data,
          timestamp: new Date().toISOString(),
        };

        localStorage.setItem("user", JSON.stringify(fullUserData));
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("tokenV2", response.data.token);
        localStorage.setItem("userId", response.data.id);

        saveprofile(fullUserData);
        await login(fullUserData);
        navigate("/dashboard/overview");
        setLoading(false);
        return;
      }

      toast.error("Email or password is incorrect.");
      setLoading(false);
    } catch (error) {
      const message = error.response?.data?.message || "Login failed";
      toast.error(message);
      setLoading(false);
    }
  };

  return (
    <div className="page-sign d-block py-0">
      <Row className="g-0 min-vh-100">
        <Col md="7" lg="5" xl="4" className="col-wrapper">
          <Card className="card-sign">
            <Card.Header>
              <div className="auth-brand">
                <span className="auth-brand-mark">
                  <img src={dedalogo} alt="Deda Hospital" />
                </span>
                <span className="auth-brand-copy">
                  <strong>Deda Hospital</strong>
                  <span>Patient portal</span>
                </span>
              </div>

              <div className="auth-kicker">Patient access</div>
              <Card.Title>Welcome back</Card.Title>
              <Card.Text>
                Sign in to manage appointments, results, medications, payments,
                and support in a cleaner, more reassuring workspace.
              </Card.Text>

              {/* Login Method Tabs */}
              <div className="d-flex p-1 bg-light rounded-3 my-3" style={{ border: "1px solid #e2e8f0" }}>
                <button
                  type="button"
                  onClick={() => setLoginMode("password")}
                  className={`btn btn-sm flex-fill py-2 fw-semibold rounded-2 transition-all ${
                    loginMode === "password"
                      ? "btn-light shadow-sm text-dark bg-white border"
                      : "text-muted border-0 bg-transparent"
                  }`}
                >
                  <FaKey className="me-2" /> Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setLoginMode("qr");
                    if (!qrSessionId && qrStatus === "idle") {
                      fetchQrSession();
                    }
                  }}
                  className={`btn btn-sm flex-fill py-2 fw-semibold rounded-2 transition-all ${
                    loginMode === "qr"
                      ? "btn-light shadow-sm text-dark bg-white border"
                      : "text-muted border-0 bg-transparent"
                  }`}
                >
                  <FaQrcode className="me-2 text-success" /> Scan QR Code
                </button>
              </div>
            </Card.Header>

            <Card.Body>
              {loginMode === "password" ? (
                <Form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your email address"
                      value={userName}
                      onChange={(event) => setUserName(event.target.value)}
                    />
                  </div>

                  <div className="mb-4">
                    <div className="position-relative">
                      <Form.Label className="d-flex justify-content-between align-items-center">
                        <span>Password</span>
                      </Form.Label>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        className="pe-5"
                      />
                      <span
                        onClick={() => setShowPassword((current) => !current)}
                        className="auth-show-password"
                      >
                        {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span></span>
                      <Link to="/pages/forgot" className="text-deda p-2">
                        Forgot password?
                      </Link>
                    </div>
                  </div>

                  <Button type="submit" className="btn-deda">
                    {loading ? <BtnSpinner /> : "Sign In"}
                  </Button>
                </Form>
              ) : (
                <div className="text-center py-1">
                  <div className="d-flex justify-content-center my-2">
                    <div
                      className="p-3 bg-white rounded-3 shadow-sm border position-relative"
                      style={{ display: "inline-block" }}
                    >
                      <QRCode
                        value={qrData || "deda-qr-session"}
                        size={220}
                        status={
                          qrStatus === "loading"
                            ? "loading"
                            : qrStatus === "expired"
                            ? "expired"
                            : "active"
                        }
                        onRefresh={fetchQrSession}
                        icon={dedaIcon}
                        iconSize={42}
                        bordered={false}
                      />
                    </div>
                  </div>

                  {qrStatus === "scanned" && (
                    <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center justify-content-center text-start">
                      <Spinner animation="border" size="sm" className="me-2 text-info" />
                      <span className="small">
                        <strong>QR Code Scanned!</strong>
                        <br />
                        Confirm login on your mobile app...
                      </span>
                    </div>
                  )}

                  {qrStatus === "pending" && (
                    <div className="text-center mb-3">
                      <span className="small text-muted">
                        Expires in <strong className="text-dark">{timeLeft}s</strong>
                      </span>
                    </div>
                  )}

                  {qrStatus === "expired" && (
                    <div className="text-center mb-3">
                      <div className="text-danger small fw-semibold mb-2">
                        QR code expired after 2 minutes for your security.
                      </div>
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="px-3 py-1 fw-semibold d-inline-flex align-items-center"
                        onClick={fetchQrSession}
                      >
                        <FaSyncAlt className="me-2" size={12} /> Reload New Code
                      </Button>
                    </div>
                  )}

                  <div
                    className="bg-light p-3 rounded-3 text-start mb-2 border"
                    style={{ fontSize: "12.5px", color: "#475569" }}
                  >
                    <div className="fw-semibold text-dark mb-2 d-flex align-items-center">
                      <FaMobileAlt className="me-2 text-success" /> How to log in with Deda Mobile:
                    </div>
                    <ol className="mb-0 ps-3">
                      <li className="mb-1">
                        Open the <strong>Deda Hospital</strong> app on your phone.
                      </li>
                      <li className="mb-1">
                        Go to <strong>Profile</strong> and tap the <strong>QR Scanner</strong> icon.
                      </li>
                      <li>Point your camera at this QR code to log in.</li>
                    </ol>
                  </div>
                </div>
              )}
            </Card.Body>


            <Card.Footer style={{ textAlign: "left" }}>
              <p className="auth-footer-note mb-2">
                Already a patient but don&apos;t have login credentials?{" "}
                <Link to="/pages/get-credencials" className="text-deda">
                  Set up your account
                </Link>
              </p>
              <p className="auth-footer-note mb-0">
                Don&apos;t have an account?{" "}
                <Link to="/pages/signup2" className="text-deda">
                  Create one
                </Link>
              </p>
            </Card.Footer>
          </Card>
        </Col>

        <Col className="d-none d-lg-block auth-container">
          <img src={dedaBg} className="auth-img" alt="Modern Deda Hospital portal" />
          <div className="auth-panel-overlay">
            <div className="auth-panel-content">
              <span>Modern patient experience</span>
              <h2>Healthcare access that feels calm, clear, and elegant.</h2>
              <p>
                Move from bookings to reports, medication history, billing, and
                support in a portal designed to reduce friction and keep the focus
                on your care.
              </p>

              <ul className="auth-feature-list">
                <li>Appointments and reminders</li>
                <li>Lab results and reports</li>
                <li>Billing and wallet access</li>
                <li>Support and AI guidance</li>
              </ul>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
